// =============================================================================
// PAGE MASTER (frontend mirror of the backend Page & Action Master)
// Backend authoritative copy: hr_backend/src/constants/accessPages.js
//
// PERMISSION IDENTITY = `pageKey` (+ action). Never rename a pageKey of an
// existing page — employee permissions in user_access are keyed by it.
//
// The fields below are PRESENTATION/BRIDGE metadata only:
//   - route        current portal route (bridge for route -> pageKey checks)
//   - division     future division-wise sidebar grouping (presentation only)
//   - displayOrder future sidebar position (presentation only)
//   - active       included in access checks & future sidebar when true
//
// Changing division / displayOrder / route presentation must NOT change any
// pageKey — existing employee permissions are unaffected by such edits.
// =============================================================================

export const PAGE_MASTER = [
  { pageKey: "dashboard", label: "TA Dashboard", route: "/dashboard", division: "TA Team", displayOrder: 1, active: true, icon: "chart",
    actions: [{ action: "view", label: "VIEW", permission: null }] },
  { pageKey: "candidate", label: "Candidates", route: "/candidates", division: "TA Team", displayOrder: 2, active: true, icon: "people",
    actions: [
      { action: "view", label: "VIEW", permission: "candidate.view" },
      { action: "add", label: "ADD", permission: "candidate.create" },
      { action: "edit", label: "EDIT", permission: "candidate.edit" },
      { action: "delete", label: "DELETE", permission: "candidate.delete" },
    ] },
  { pageKey: "job", label: "Jobs", route: "/jobs", division: "TA Team", displayOrder: 3, active: true, icon: "briefcase",
    actions: [
      { action: "view", label: "VIEW", permission: "job.manage" },
      { action: "add", label: "ADD", permission: "job.manage" },
      { action: "edit", label: "EDIT", permission: "job.edit" },
      { action: "export", label: "EXPORT", permission: "job.export" },
    ] },
  { pageKey: "interview", label: "Interviews", route: "/interviews", division: "TA Team", displayOrder: 4, active: true, icon: "chat",
    actions: [
      { action: "view", label: "VIEW", permission: "interview.manage" },
      { action: "manage", label: "MANAGE", permission: "interview.manage" },
    ] },
  { pageKey: "ai", label: "AI Assistant", route: "/ai", division: "TA Team", displayOrder: 5, active: true, icon: "star",
    actions: [
      { action: "view", label: "VIEW", permission: null },
      { action: "use", label: "USE", permission: null },
    ] },
  { pageKey: "employee", label: "Employees", route: "/employees", division: "HR Team", displayOrder: 6, active: true, icon: "people",
    actions: [
      { action: "view", label: "VIEW", permission: "employee.view" },
      { action: "add", label: "ADD", permission: "employee.create" },
      { action: "edit", label: "EDIT", permission: "employee.edit" },
      { action: "delete", label: "DELETE", permission: "employee.delete" },
    ] },
  { pageKey: "trainee", label: "Trainee", route: "/trainees", division: "HR Team", displayOrder: 7, active: false, icon: "graduation",
    actions: [
      { action: "view", label: "VIEW", permission: "trainee.view" },
      { action: "add", label: "ADD", permission: "trainee.create" },
      { action: "edit", label: "EDIT", permission: "trainee.edit" },
      { action: "delete", label: "DELETE", permission: "trainee.delete" },
      { action: "export", label: "EXPORT", permission: "trainee.export" },
    ] },
  { pageKey: "intern", label: "Intern", route: "/interns", division: "HR Team", displayOrder: 8, active: false, icon: "graduation",
    actions: [
      { action: "view", label: "VIEW", permission: "intern.view" },
      { action: "add", label: "ADD", permission: "intern.create" },
      { action: "edit", label: "EDIT", permission: "intern.edit" },
      { action: "delete", label: "DELETE", permission: "intern.delete" },
      { action: "export", label: "EXPORT", permission: "intern.export" },
    ] },
  { pageKey: "offer", label: "Offers", route: "/offers", division: "HR Team", displayOrder: 8, active: true, icon: "file",
    actions: [
      { action: "view", label: "VIEW", permission: "offer.view" },
      { action: "create", label: "CREATE", permission: "offer.create" },
      { action: "manage", label: "MANAGE", permission: "offer.manage" },
    ] },
  { pageKey: "offer_doc", label: "Document Vault", route: "/offer-documents", division: "HR Team", displayOrder: 9, active: true, icon: "file",
    actions: [{ action: "view", label: "VIEW", permission: "offer.documents" }] },
  { pageKey: "delegation", label: "Delegations", route: "/delegations", division: "HR Team", displayOrder: 10, active: true, icon: "people",
    actions: [
      { action: "view", label: "VIEW", permission: "offer.view" },
      { action: "manage", label: "MANAGE", permission: "offer.manage" },
    ] },
  { pageKey: "payroll", label: "Revenue Tracker", route: "/payrolltracker", division: "Finance Team", displayOrder: 11, active: true, icon: "file",
    actions: [
      { action: "view", label: "VIEW", permission: "payroll.view" },
      { action: "edit", label: "EDIT", permission: "payroll.edit" },
      { action: "export", label: "EXPORT", permission: "payroll.export" },
    ] },
  { pageKey: "variable", label: "Variable Tracker", route: "/variabletracker", division: "Finance Team", displayOrder: 12, active: true, icon: "chart",
    actions: [
      { action: "view", label: "VIEW", permission: "variable.view" },
      { action: "edit", label: "EDIT", permission: "variable.edit" },
      { action: "export", label: "EXPORT", permission: "variable.export" },
    ] },
  { pageKey: "resource_cost", label: "Project & Financial MIS", route: "/project-mis", division: "Finance Team", displayOrder: 13, active: true, icon: "folder",
    actions: [
      { action: "view", label: "VIEW", permission: "resource_cost.view" },
      { action: "edit", label: "EDIT", permission: "resource_cost.edit" },
      { action: "export", label: "EXPORT", permission: "resource_cost.export" },
    ] },
  { pageKey: "customer", label: "Customers", route: "/customers", division: "Finance Team", displayOrder: 14, active: true, icon: "folder",
    actions: [
      { action: "view", label: "VIEW", permission: "customer.view" },
      { action: "add", label: "ADD", permission: "customer.create" },
      { action: "edit", label: "EDIT", permission: "customer.edit" },
      { action: "delete", label: "DELETE", permission: "customer.delete" },
    ] },
  { pageKey: "project", label: "Projects", route: "/projects", division: "Finance Team", displayOrder: 15, active: true, icon: "folder",
    actions: [
      { action: "view", label: "VIEW", permission: "project.view" },
      { action: "add", label: "ADD", permission: "project.create" },
      { action: "edit", label: "EDIT", permission: "project.edit" },
      { action: "delete", label: "DELETE", permission: "project.delete" },
    ] },
  { pageKey: "sales", label: "Sales", route: "/sales", division: "Sales Team", displayOrder: 16, active: true, icon: "chart",
    actions: [
      { action: "view", label: "VIEW", permission: "sales.view" },
      { action: "create", label: "CREATE", permission: "sales.create" },
      { action: "edit", label: "EDIT", permission: "sales.edit" },
      { action: "delete", label: "DELETE", permission: "sales.delete" },
      { action: "import", label: "IMPORT", permission: "sales.import" },
    ] },
  { pageKey: "master", label: "Masters", route: "/masters", division: "Admin Team", displayOrder: 17, active: true, icon: "folder",
    actions: [
      { action: "view", label: "VIEW", permission: "master.view" },
      { action: "manage", label: "MANAGE", permission: "master.manage" },
    ] },
  { pageKey: "headcount", label: "HC Dashboard", route: "/headcount", division: "HR Team", displayOrder: 18, active: true, icon: "chart",
    actions: [
      { action: "view", label: "VIEW", permission: "headcount.view" },
      { action: "export", label: "EXPORT", permission: "headcount.export" },
      { action: "import", label: "IMPORT", permission: "headcount.import" },
      { action: "sync", label: "SYNC", permission: "headcount.sync" },
    ] },
  { pageKey: "support_ticket", label: "Support Tickets", route: "/support-tickets", division: "Home / Global", displayOrder: 19, active: true, icon: "support",
    actions: [
      { action: "view", label: "VIEW", permission: "support_ticket.view" },
      { action: "create", label: "RAISE", permission: "support_ticket.create" },
      { action: "assign", label: "ASSIGN", permission: "support_ticket.assign" },
      { action: "close", label: "CLOSE", permission: "support_ticket.close" },
    ] },
  { pageKey: "reimbursement", label: "Reimbursement", route: "/reimbursement", division: "Home / Global", displayOrder: 20, active: true, icon: "receipt",
    actions: [
{ action: "view", label: "VIEW", permission: "reimbursement.view" },
      { action: "create", label: "CREATE", permission: "reimbursement.create" },
      { action: "approve", label: "APPROVE (Project Owner)", permission: "reimbursement.approve" },
      { action: "reject", label: "REJECT (Project Owner)", permission: "reimbursement.reject" },
      { action: "verify", label: "VERIFY (Finance)", permission: "reimbursement.verify" },
      { action: "process", label: "PROCESS", permission: "reimbursement.process" },
    ] },
  { pageKey: "reimbursement_dashboard", label: "Reimbursement Dashboard", route: "/reimbursement-dashboard-access", division: "Home / Global", displayOrder: 22, active: true, icon: "chart",
    actions: [{ action: "view", label: "VIEW", permission: "reimbursement.dashboard.view" }] },
  { pageKey: "reimbursement_approved", label: "Reimbursement Approved", route: "/reimbursement-approved-access", division: "Home / Global", displayOrder: 23, active: true, icon: "receipt",
    actions: [{ action: "view", label: "VIEW", permission: "reimbursement.approved.view" }] },
  { pageKey: "my_performance", label: "My Performance", route: "/my-performance", division: "TA Team", displayOrder: 20, active: true, icon: "chart",
    actions: [
      { action: "view", label: "VIEW", permission: "performance.view" },
    ] },
  { pageKey: "analytics", label: "Analytics", route: "/admin/analytics", division: "Sudo Access", displayOrder: 21, active: true, icon: "chart",
    actions: [
      { action: "view", label: "VIEW", permission: "analytics.view" },
    ] },
];

// Active pages in displayOrder — the list a future division-wise sidebar will
// group by `division` and sort by `displayOrder`.
export const ACTIVE_PAGES = PAGE_MASTER.filter((p) => p.active).sort(
  (a, b) => a.displayOrder - b.displayOrder
);

// Distinct division names in displayOrder sequence.
export const DIVISIONS = [...new Set(ACTIVE_PAGES.map((p) => p.division))];
